/**
 * H-21 — Offline sale queue.
 *
 * When a sale can't reach the server (device offline / network drop), the sale
 * payload is stored in IndexedDB and replayed automatically when connectivity
 * returns. Every sale already carries a client-generated `idempotency_key`, so
 * replay is safe: if the server actually processed a sale whose response was
 * lost, replaying the same key returns the existing invoice instead of creating
 * a duplicate (server replay-guard in stock_out.process_sale).
 *
 * Reliability choices:
 *  - Pure IndexedDB, no library (no new dependency).
 *  - Replay is triggered by the `online` event and on app load — NOT the Service
 *    Worker Background Sync API, which is unreliable/unsupported across the
 *    browsers Indian shop devices actually run. (SW background sync can be added
 *    later as an enhancement.)
 *  - A sale is queued ONLY on a genuine network failure (no HTTP response) or
 *    navigator.onLine === false. A server error (any HTTP status) is NEVER
 *    queued — it surfaces to the cashier so money/stock errors aren't hidden.
 *  - A queued sale that the server REJECTS on replay (e.g. 422 insufficient
 *    stock) is marked `failed` and kept for the owner to resolve — never
 *    silently dropped.
 *  - PH-67: a 401/403 on replay is NOT a rejection of the sale — it means nobody
 *    with the right to sell is signed in right now (no session, an expired or
 *    revoked cookie, a stock-only login). The record stays `queued` untouched and
 *    the drain stops after that one attempt; the next signed-in drain sends it.
 *    Records an older build wrongly stamped `failed` for that reason are put back
 *    by `healAuthFailures` on app load.
 *  - PH-67 (store tag): the server books a replayed sale to the store of the
 *    session that sends it, not the store it was rung up in. So every record
 *    carries `warehouse_id` — the store of the session signed in when it was
 *    queued — and a drain only sends records whose tag matches the store signed
 *    in now. A record for another store stays `queued`, untouched, until someone
 *    signs in to that store. A record with no tag (queued by an older build) is
 *    HELD: never sent as it is, because nothing on the device says which store
 *    it belongs to — unless the shop has exactly one active store, in which case
 *    `adoptUntaggedSales` stamps it with that store first (no wrong store exists).
 */

const DB_NAME = 'pos-offline';
const DB_VERSION = 1;
const STORE = 'queued_sales';

function openDb() {
  return new Promise((resolve, reject) => {
    let req;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      reject(e);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        // keyPath = idempotency_key → enqueuing the same sale twice is a no-op (put overwrites)
        db.createObjectStore(STORE, { keyPath: 'idempotency_key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Runs a SINGLE-REQUEST callback inside one transaction. `fn` must issue exactly
// one IndexedDB request synchronously (put/delete/getAll) and NOT await between
// requests — IndexedDB auto-commits the transaction the moment the event loop
// yields with no pending requests, so a multi-step async fn would write into a
// committed (inactive) transaction. Multi-step writes use their own functions
// (see markFailed) that chain requests via onsuccess, never via await.
async function tx(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    let settled = false;
    const done = (f, v) => { if (!settled) { settled = true; db.close(); f(v); } };
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    let result;
    Promise.resolve(fn(store)).then((r) => { result = r; }).catch((e) => done(reject, e));
    t.oncomplete = () => done(resolve, result);
    t.onerror = () => done(reject, t.error);
    t.onabort = () => done(reject, t.error);
  });
}

const reqToPromise = (req) => new Promise((res, rej) => {
  req.onsuccess = () => res(req.result);
  req.onerror = () => rej(req.error);
});

/**
 * PH-67 — the store (warehouse id) of the session signed in on this device, as a
 * string, or null when there is none. Read from the same `session` record App.jsx
 * restores its current store from, and which every login path writes from the
 * login response (the same `warehouse_id` the server puts in the session cookie).
 * Warehouse ids are database-wide keys, so they never collide across shops.
 */
export function currentStoreId() {
  try {
    const s = JSON.parse(localStorage.getItem('session') || 'null');
    return storeOf(s);
  } catch {
    return null;
  }
}

/** A record's (or session's) store as a string, or null when it has none — an untagged older record. */
function storeOf(rec) {
  const w = rec && rec.warehouse_id;
  return w === null || w === undefined || w === '' ? null : String(w);
}

/**
 * Store a sale payload for later replay. Keyed by idempotency_key (idempotent).
 * PH-67: tagged with the store of the session signed in right now, so it is only
 * ever replayed into that store.
 */
export async function enqueueSale(payload, warehouseId = currentStoreId()) {
  const record = {
    idempotency_key: payload.idempotency_key,   // keyPath — must be present
    payload,
    status: 'queued',                            // queued | failed
    failed_reason: null,
    queued_at: new Date().toISOString(),
    warehouse_id: storeOf({ warehouse_id: warehouseId }),
  };
  if (!record.idempotency_key) {
    throw new Error('enqueueSale: payload missing idempotency_key');
  }
  await tx('readwrite', (store) => reqToPromise(store.put(record)));
  return record;
}

/** All queued/failed records (newest first). */
export async function getQueued() {
  const all = await tx('readonly', (store) => reqToPromise(store.getAll()));
  return (all || []).sort((a, b) => (b.queued_at || '').localeCompare(a.queued_at || ''));
}

/**
 * Counts for the UI badge: { queued, failed, otherStore, unknownStore }.
 * `queued` is every waiting record. PH-67: with a store signed in, `otherStore`
 * counts the waiting records tagged for a different store and `unknownStore` the
 * untagged ones (both are inside `queued`, and neither is sent from here). With no
 * store signed in both are 0 — there is nothing to compare against.
 */
export async function queueCounts(warehouseId = currentStoreId()) {
  try {
    const all = await getQueued();
    const waiting = all.filter((r) => r.status === 'queued');
    const here = storeOf({ warehouse_id: warehouseId });
    return {
      queued: waiting.length,
      failed: all.filter((r) => r.status === 'failed').length,
      otherStore: here === null ? 0 : waiting.filter((r) => storeOf(r) !== null && storeOf(r) !== here).length,
      unknownStore: here === null ? 0 : waiting.filter((r) => storeOf(r) === null).length,
    };
  } catch {
    return { queued: 0, failed: 0, otherStore: 0, unknownStore: 0 };
  }
}

async function removeByKey(key) {
  await tx('readwrite', (store) => reqToPromise(store.delete(key)));
}

async function markFailed(key, reason) {
  // Chain get -> put via onsuccess (NOT await) so both requests stay within the
  // same still-open transaction. Using `await` between them would let IndexedDB
  // auto-commit the transaction, and the put would throw TransactionInactiveError
  // (Firefox) or silently no-op (Chrome) — leaving the record stuck as 'queued'
  // and replaying forever.
  const db = await openDb();
  return new Promise((resolve, reject) => {
    let settled = false;
    const done = (f, v) => { if (!settled) { settled = true; db.close(); f(v); } };
    const t = db.transaction(STORE, 'readwrite');
    const store = t.objectStore(STORE);
    const getReq = store.get(key);
    getReq.onsuccess = () => {
      const rec = getReq.result;
      if (rec) {
        rec.status = 'failed';
        rec.failed_reason = reason;
        const putReq = store.put(rec);   // same event-loop turn → transaction stays open
        // Defensive: on old Android WebViews a put error doesn't always bubble to
        // t.onabort. Force an abort so we reject (not silently resolve with the
        // record still 'queued', which would replay forever).
        putReq.onerror = (e) => { if (e && e.stopPropagation) e.stopPropagation(); try { t.abort(); } catch (_) {} };
      }
    };
    getReq.onerror = () => { try { t.abort(); } catch (_) {} };
    t.oncomplete = () => done(resolve);
    t.onerror = () => done(reject, t.error);
    t.onabort = () => done(reject, t.error);
  });
}

/**
 * PH-67 — the exact `detail` strings the backend sends with a 401/403 on
 * POST /stock-out/sale (app/auth.py get_current_user / _refresh_staff_claims /
 * require_sales, and stock_out.py's store-context check), plus axios's own message
 * for a 401/403 that carried no detail. These are what an older build stored as
 * `failed_reason` when it mistook "not signed in" for "sale rejected". Exact match
 * only: a 422 reason ("Insufficient stock ...", a discount cap) can never be one of
 * these, so a genuine rejection is never put back in the queue.
 */
export const AUTH_FAILURE_REASONS = Object.freeze([
  'Not authenticated',
  'Invalid or expired token',
  'Token has been revoked',
  'Store no longer exists',
  'Account no longer exists',
  'Session invalidated — please log in again',
  'आपका नाम अब स्टाफ सूची में सक्रिय नहीं है — फिर से लॉग इन करें / Your name is no longer active on the staff list — please log in again',
  'आपकी भूमिकाएँ हटा दी गई हैं — फिर से लॉग इन करें / Your access has changed — please log in again',
  'अभी किसी स्टोर की अनुमति नहीं है — मालिक से पूछें / You are not allowed into any store yet — ask the owner',
  'Sales or admin access required',
  'Warehouse context required — please log in by selecting a store.',
  'Request failed with status code 401',
  'Request failed with status code 403',
]);
const _authReasonSet = new Set(AUTH_FAILURE_REASONS);

/**
 * PH-67 — put back to `queued` every record an older build stamped `failed` for an
 * auth reason. One transaction, requests chained in the getAll callback (never
 * awaited, see markFailed). Idempotent: once clean it reads and writes nothing.
 * Resolves to the number of records put back.
 */
export async function healAuthFailures() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    let settled = false;
    let healed = 0;
    const done = (f, v) => { if (!settled) { settled = true; db.close(); f(v); } };
    const t = db.transaction(STORE, 'readwrite');
    const store = t.objectStore(STORE);
    const getReq = store.getAll();
    getReq.onsuccess = () => {
      for (const rec of getReq.result || []) {
        if (rec.status === 'failed' && _authReasonSet.has(rec.failed_reason)) {
          rec.status = 'queued';
          rec.failed_reason = null;
          store.put(rec);                          // same callback → transaction still open
          healed += 1;
        }
      }
    };
    t.oncomplete = () => done(resolve, healed);
    t.onerror = () => done(reject, t.error);
    t.onabort = () => done(reject, t.error);
  });
}

/**
 * PH-67 — the one safe release for untagged (older-build) waiting sales: when the
 * shop has exactly ONE active store there is no wrong store for them to land in,
 * so each is stamped with that store and the next drain sends it. With two or
 * more stores, or when the store list cannot be read (offline, an error, a login
 * that may not read it — GET /tenant/warehouses is admin-only), they stay held.
 * The single store must also be the store signed in now, or nothing is stamped.
 *
 * `fetchStores` is the existing warehouses read (api.getWarehouses); it is only
 * called when an untagged waiting record exists. Resolves to the number stamped;
 * never rejects — any failure means "hold".
 */
export async function adoptUntaggedSales(fetchStores) {
  try {
    const all = await getQueued();
    if (!all.some((r) => r.status === 'queued' && storeOf(r) === null)) return 0;
    const res = await fetchStores();
    const rows = Array.isArray(res && res.data) ? res.data : null;
    if (!rows) return 0;
    const stores = rows.filter((w) => w && w.type === 'Store' && Number(w.is_active) === 1);
    if (stores.length !== 1) return 0;
    const only = storeOf({ warehouse_id: stores[0].id });
    if (only === null || only !== currentStoreId()) return 0;
    return await stampUntagged(only);
  } catch {
    return 0;
  }
}

// One transaction, requests chained in the getAll callback (never awaited, see markFailed).
async function stampUntagged(warehouseId) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    let settled = false;
    let stamped = 0;
    const done = (f, v) => { if (!settled) { settled = true; db.close(); f(v); } };
    const t = db.transaction(STORE, 'readwrite');
    const store = t.objectStore(STORE);
    const getReq = store.getAll();
    getReq.onsuccess = () => {
      for (const rec of getReq.result || []) {
        if (rec.status === 'queued' && storeOf(rec) === null) {
          rec.warehouse_id = warehouseId;
          store.put(rec);                          // same callback → transaction still open
          stamped += 1;
        }
      }
    };
    t.oncomplete = () => done(resolve, stamped);
    t.onerror = () => done(reject, t.error);
    t.onabort = () => done(reject, t.error);
  });
}

/** Discard a failed sale the owner has chosen to abandon. */
export async function discardFailed(key) {
  await removeByKey(key);
}

/**
 * Treat an error from the raw sale POST as a transient network failure.
 * True when the request never got an HTTP response (offline, DNS, reset,
 * timeout). False for any server status — those are real, surfaced errors.
 */
export function isNetworkError(err) {
  if (!err) return false;
  if (err.response) return false;                 // got an HTTP status → real error, surface it
  // A CORS rejection looks like a bare TypeError (no request/response). Treat it as
  // surfaceable, NOT offline — otherwise a misconfigured backend silently queues sales.
  if (!err.request && err.message && /cors/i.test(err.message)) return false;
  if (err.request) return true;                   // request was sent, no response → offline/drop
  if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') return true;
  // Message heuristic only for genuine axios errors — avoids misclassifying a
  // random programming error whose message happens to contain "network"/"timeout".
  if (err.isAxiosError && err.message && /network|timeout|fetch/i.test(err.message)) return true;
  return false;
}

/**
 * PH-67 — a 401/403 from the sale POST: the server did not look at the sale, it
 * refused the caller (no session, expired/revoked cookie, a login without the
 * sales job). Not the sale's fault, so never a reason to mark it `failed`.
 */
export function isAuthError(err) {
  const status = err && err.response && err.response.status;
  return status === 401 || status === 403;
}

/**
 * Drain the queue by replaying each queued sale through `rawProcessSale`
 * (the un-wrapped network POST — passing the wrapped one would re-enqueue on
 * failure and loop forever).
 *
 * Returns { synced, failed, remaining, authBlocked, held }. Stops early on a network
 * failure (still offline) or a 401/403 (nobody allowed to sell is signed in),
 * leaving that record and the rest queued for the next attempt.
 *
 * PH-67: only records tagged with the store signed in now (read from the
 * session at drain time, and re-checked before every send) are sent. Records for another
 * store and untagged records are not touched. Returns `held` = how many waiting
 * records were left alone for that reason.
 */
let _syncInFlight = false;

export async function syncQueue(rawProcessSale) {
  // Guard against two concurrent drains (the `online` event and the app-load
  // effect can both fire). Without this, both would replay the same record;
  // the server's uq_sale_idempotency constraint prevents a double invoice, but
  // the loser's IntegrityError would wrongly mark an already-synced sale 'failed'.
  if (_syncInFlight) return { synced: 0, failed: 0, remaining: (await queueCounts()).queued };
  _syncInFlight = true;
  try {
    return await _syncQueueInner(rawProcessSale, currentStoreId());
  } finally {
    _syncInFlight = false;
  }
}

async function _syncQueueInner(rawProcessSale, here) {
  const all = await getQueued();
  const waiting = all.filter((r) => r.status === 'queued');
  // PH-67: never replay a sale into a store it was not rung up in. No store signed
  // in → nothing matches; an untagged record (storeOf → null) never matches.
  const pending = here === null ? [] : waiting.filter((r) => storeOf(r) === here);
  const held = waiting.length - pending.length;
  let synced = 0;
  let failed = 0;
  let authBlocked = false;
  for (const rec of pending) {
    // The session can change mid-drain (sign-out, sign-in to another store). The
    // server books by the session that sends, so stop the moment it no longer
    // matches the store this pass was started for.
    if (here !== currentStoreId()) break;
    try {
      await rawProcessSale(rec.payload);          // same idempotency_key → replay-safe
    } catch (err) {
      if (isNetworkError(err)) {
        break;                                    // still offline — keep the rest, retry later
      }
      if (isAuthError(err)) {
        // PH-67: nobody allowed to sell is signed in. Leave this record and every
        // other one exactly as they are, and stop — one 401 must not walk the queue.
        authBlocked = true;
        break;
      }
      // Server rejected it (e.g. 422 insufficient stock). Don't drop — flag it.
      const reason = err.response?.data?.detail || err.message || 'Server rejected the sale';
      try {
        await markFailed(rec.idempotency_key, String(reason));
        failed += 1;
      } catch (_) {
        // PH-67: the flag could not be written. The record stays `queued` and is
        // replayed (and re-flagged) next drain — never a reason to abandon the rest.
      }
      continue;
    }
    // Sent. Removing it locally is separate from the send on purpose: a failed
    // delete must not fall into the "server rejected it" branch above and stamp a
    // sale the server already took as `failed`. Left `queued`, the next replay of
    // the same idempotency_key returns the existing invoice.
    try {
      await removeByKey(rec.idempotency_key);
    } catch (_) { /* replay-safe, see above */ }
    synced += 1;
  }
  const after = await queueCounts();
  return { synced, failed, remaining: after.queued, authBlocked, held };
}
